/**
 * SPA hooks for M3 FBA shipments — module-gated on 'shipments'. Every hook
 * assumes the caller sits behind useModuleEnabled('shipments').
 *
 * Also home to a few pure badge-logic helpers (shortage / unallocated /
 * aging) so ShipmentsPage's attention-badge rules are unit-testable without
 * rendering — see shipments.test.ts.
 */
import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateFbaShipmentResponse,
  FbaShipmentDetail,
  ListFbaShipmentsResponse,
  ShipmentCase,
  type CreateFbaShipmentRequest,
  type CreateShipmentCaseRequest,
  type FbaShipmentLineDetail,
  type FbaShipmentStatus,
  type FbaShipmentSummary,
  type RecordFbaReceivedRequest,
  type ShipmentCaseStatus,
  type UpdateShipmentCaseRequest,
} from '@sourcito/schemas';
import { apiFetch, ApiCallError } from './api';

export function useFbaShipments(enabled = true) {
  return useQuery({
    queryKey: ['fba-shipments'] as const,
    enabled,
    queryFn: () => apiFetch('/api/shipments', { responseSchema: ListFbaShipmentsResponse }),
  });
}

export function useFbaShipment(id: string | null) {
  return useQuery({
    queryKey: ['fba-shipment', id] as const,
    enabled: id != null,
    queryFn: () => apiFetch(`/api/shipments/${id}`, { responseSchema: FbaShipmentDetail }),
  });
}

export function useCreateShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFbaShipmentRequest) =>
      apiFetch('/api/shipments', {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: CreateFbaShipmentResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fba-shipments'] });
    },
  });
}

export function useMarkShipped(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch(`/api/shipments/${id}/shipped`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fba-shipments'] });
      qc.invalidateQueries({ queryKey: ['fba-shipment', id] });
    },
  });
}

export function useRecordReceived(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordFbaReceivedRequest) =>
      apiFetch<{ id: string }>(`/api/shipments/${id}/received`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fba-shipments'] });
      qc.invalidateQueries({ queryKey: ['fba-shipment', id] });
    },
  });
}

// ─────────────────────────────────────────────────────────
// Shipment cases (Phase B3) — Seller Central case numbers hand-attached to
// a shipment. No automated Amazon writes: cases are opened in Seller
// Central, this only tracks status/notes against them.
// ─────────────────────────────────────────────────────────

export function useCreateShipmentCase(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateShipmentCaseRequest) =>
      apiFetch(`/api/shipments/${id}/cases`, {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: ShipmentCase,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fba-shipment', id] });
      qc.invalidateQueries({ queryKey: ['fba-shipments'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateShipmentCase(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, ...input }: UpdateShipmentCaseRequest & { caseId: string }) =>
      apiFetch(`/api/shipments/${id}/cases/${caseId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        responseSchema: ShipmentCase,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fba-shipment', id] });
      qc.invalidateQueries({ queryKey: ['fba-shipments'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteShipmentCase(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) =>
      apiFetch<null>(`/api/shipments/${id}/cases/${caseId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fba-shipment', id] });
      qc.invalidateQueries({ queryKey: ['fba-shipments'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export interface UploadShipmentFileInput {
  shipmentId: string;
  file: File;
}

export interface UploadShipmentFileResult {
  id: string;
  filename: string;
}

/**
 * Multipart upload — bypasses apiFetch (which auto-sets Content-Type:
 * application/json) so the browser can set the multipart boundary itself.
 * Mirrors useUploadPricingSheet's fetch/FormData approach.
 */
export async function uploadShipmentFile({
  shipmentId,
  file,
}: UploadShipmentFileInput): Promise<UploadShipmentFileResult> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  const res = await fetch(`/api/shipments/${shipmentId}/files`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      (json as { error?: { message?: string } })?.error?.message ??
      `Upload failed with status ${res.status}`;
    throw new ApiCallError(res.status, message, json);
  }
  return json as UploadShipmentFileResult;
}

export function useUploadShipmentFile(shipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadShipmentFile({ shipmentId, file }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fba-shipment', shipmentId] });
    },
  });
}

// ─────────────────────────────────────────────────────────
// Pure badge-logic helpers
// ─────────────────────────────────────────────────────────

/**
 * "N units short at FBA" — only fires once every line has been checked in
 * (anyUnreceived === false); otherwise a partial check-in would look like a
 * shortage before receiving is even done.
 */
export function shortageBadgeLabel(
  s: Pick<FbaShipmentSummary, 'totalQty' | 'totalReceived' | 'anyUnreceived'>
): string | null {
  if (s.anyUnreceived) return null;
  const short = s.totalQty - s.totalReceived;
  if (short <= 0) return null;
  return `${short} unit${short === 1 ? '' : 's'} short at FBA`;
}

/** "N unallocated" — units on the shipment that couldn't be lot-costed from stock. */
export function unallocatedBadgeLabel(unallocatedUnits: number): string | null {
  if (unallocatedUnits <= 0) return null;
  return `${unallocatedUnits} unallocated`;
}

export type ReceivedStatusVariant = 'quiet' | 'positive' | 'warning' | 'negative';

export interface ReceivedStatus {
  variant: ReceivedStatusVariant;
  /** The "→ ..." fragment rendered next to "shipped N". */
  text: string;
  /** Tooltip shown only on the shortfall (negative) case. */
  title?: string;
}

/**
 * Per-line shipped-vs-received status (M2/M3 display fix: Amazon's FBA
 * check-in count next to what was shipped). `fbaReceivedQty` is null until
 * Amazon checks the line in; a partial check-in (received < shipped) is a
 * shortfall, an exact match is the happy path, and received > shipped is a
 * rare overage — still worth flagging, not an error.
 */
export function receivedLineStatus(qty: number, fbaReceivedQty: number | null): ReceivedStatus {
  if (fbaReceivedQty == null) {
    return { variant: 'quiet', text: '→ awaiting check-in' };
  }
  if (fbaReceivedQty < qty) {
    const delta = qty - fbaReceivedQty;
    return {
      variant: 'negative',
      text: `→ received ${fbaReceivedQty} (−${delta})`,
      title: `Amazon received ${delta} fewer unit${delta === 1 ? '' : 's'} than shipped`,
    };
  }
  if (fbaReceivedQty === qty) {
    return { variant: 'positive', text: `→ received ${fbaReceivedQty}` };
  }
  return { variant: 'warning', text: `→ received ${fbaReceivedQty}` };
}

/**
 * Sum of shortfalls (shipped - received, floored at 0) across a shipment's
 * lines — drives the "N units short" header chip on the shipment detail.
 * Unlike `shortageBadgeLabel` (list-level, only fires once every line is
 * checked in), this fires as soon as any individual line is short.
 */
export function lineShortfallTotal(
  lines: { qty: number; fbaReceivedQty: number | null }[]
): number {
  return lines.reduce((sum, l) => {
    if (l.fbaReceivedQty == null) return sum;
    return sum + Math.max(l.qty - l.fbaReceivedQty, 0);
  }, 0);
}

export interface ShipmentCaseBadge {
  label: string;
  variant: 'warning' | 'positive' | 'negative';
}

/** Status badge for a shipment case row — 'open' is still pending an
 *  outcome from Amazon (warning), 'resolved' paid out (positive), 'denied'
 *  closed with no recovery (negative). */
export function shipmentCaseStatusBadge(status: ShipmentCaseStatus): ShipmentCaseBadge {
  switch (status) {
    case 'open':
      return { label: 'Open', variant: 'warning' };
    case 'resolved':
      return { label: 'Resolved', variant: 'positive' };
    case 'denied':
      return { label: 'Denied', variant: 'negative' };
  }
}

/** ShipmentRecovery.netUnrecoveredCents is RAW (shortfall − reimbursed) and
 *  may go negative when reimbursements exceed the lot-costed shortfall —
 *  the web floors at 0 for display so an over-recovered shipment never
 *  shows a negative "unrecovered" figure. */
export function netUnrecoveredForDisplay(cents: number): number {
  return Math.max(0, cents);
}

const AGING_DAYS = 30;

/** Shipment has been in transit/receiving longer than AGING_DAYS with no closure. */
export function isAgingShipment(
  status: FbaShipmentStatus,
  createdAt: string,
  now: Date = new Date()
): boolean {
  // 'preparing' ages too — a shipment recorded but never marked shipped is
  // exactly the stuck case the badge exists for. Only 'closed' is done.
  if (status === 'closed') return false;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return false;
  const days = (now.getTime() - created.getTime()) / (24 * 60 * 60 * 1000);
  return days > AGING_DAYS;
}

// ─────────────────────────────────────────────────────────
// ShipmentsPage list row formatting (Faraz, 2026-08-25: "one cryptic line
// with dates and some number"). `name` is almost always Amazon's
// auto-generated junk ("FBA (02/11/2025 21:14)-1"), so the row only shows it
// when a human actually typed something. Search (matchesShipmentSearch,
// below) still matches on the RAW name — these helpers are display-only.
// ─────────────────────────────────────────────────────────

/** Amazon Seller Central's default inbound-shipment name: "FBA (MM/DD/YYYY
 *  HH:MM[ AM/PM])" with an optional "-N" suffix for split shipments. */
const AMAZON_AUTO_SHIPMENT_NAME_RE = /^FBA \(\d{1,2}\/\d{1,2}\/\d{2,4}[^)]*\)(-\d+)?$/;

/** Human-entered shipment name for the list row, or null when there's
 *  nothing worth showing next to the shipment id — no name, Amazon's
 *  auto-generated timestamp name, or a name that just duplicates the id. */
export function displayShipmentName(name: string | null, shipmentId: string): string | null {
  if (!name) return null;
  if (name === shipmentId) return null;
  if (AMAZON_AUTO_SHIPMENT_NAME_RE.test(name)) return null;
  return name;
}

/**
 * "8-24-2026" — numeric M-D-YYYY (no zero-padding, year always included)
 * for the list row's fixed-position right-edge date, next to the status
 * badge (Faraz, 2026-08-25: a stable position there beats an in-sentence
 * date for fast visual scanning). UTC fields so the label is deterministic
 * regardless of local timezone. Shared by ShipmentsPage and ReceivingPage's
 * history rows — callers pick which date field feeds it (e.g. shippedAt ??
 * createdAt, or receivedAt).
 */
export function shortDateLabel(iso: string): string {
  const d = new Date(iso);
  return `${d.getUTCMonth() + 1}-${d.getUTCDate()}-${d.getUTCFullYear()}`;
}

/** Stage-aware qty fragment for the list row second line:
 *  - not yet shipped: "108 units across 3 lines"
 *  - fully received (totalReceived >= totalQty > 0): "108 units received in full"
 *  - otherwise (shipped/receiving, or partially received): "45 of 108 units received"
 */
export function shipmentQtyStory(
  s: Pick<FbaShipmentSummary, 'shippedAt' | 'totalQty' | 'totalReceived' | 'lineCount'>
): string {
  const unit = (n: number) => `${n} unit${n === 1 ? '' : 's'}`;
  const line = (n: number) => `${n} line${n === 1 ? '' : 's'}`;

  if (s.totalQty > 0 && s.totalReceived >= s.totalQty) {
    return `${unit(s.totalQty)} received in full`;
  }
  if (!s.shippedAt && s.totalReceived === 0) {
    return `${unit(s.totalQty)} across ${line(s.lineCount)}`;
  }
  return `${s.totalReceived} of ${unit(s.totalQty)} received`;
}

/**
 * Whether the manual FBA check-in form should render on the shipment detail
 * (Faraz, 2026-08-25: a closed, fully-received shipment still showed an
 * empty "FBA received qty" box, which read as "received qty is missing").
 * Closed is the terminal state — nothing left to check in, so no form. The
 * API itself does NOT reject a check-in on a closed shipment (recordReceived
 * has no status guard and would just re-close it), so this is purely a UI
 * honesty rule, not a 409 mirror.
 */
export function canRecordCheckIn(status: FbaShipmentStatus): boolean {
  return status !== 'closed';
}

/** Text portion of a collapsed-row line-preview entry — the sellerSku
 *  renders separately in its own `font-mono` span, this is everything after
 *  it: the product name (or a placeholder when the SKU isn't mapped to a
 *  synced Amazon listing yet) plus the shipped qty. */
export function shipmentLinePreviewLabel(productName: string | null, qty: number): string {
  return `${productName ?? 'unmapped product'} ×${qty}`;
}

/** "+N more line(s)" trailer under a collapsed row's line preview, or null
 *  once every line on the shipment already fits inside the preview shown. */
export function shipmentPreviewMoreLabel(lineCount: number, previewLength: number): string | null {
  const more = lineCount - previewLength;
  if (more <= 0) return null;
  return `+${more} more line${more === 1 ? '' : 's'}`;
}

// ─────────────────────────────────────────────────────────
// ShipmentsPage toolbar: search (shipment id / name / line seller_sku+
// productName) + column sort (shippedAt, status, shipment id, units).
// Mirrors amazon.ts's search+sort pattern (see AmazonPage/lib/amazon.ts).
// ─────────────────────────────────────────────────────────

export type ShipmentSearchLine = Pick<FbaShipmentLineDetail, 'sellerSku' | 'productName'>;

/** Case-insensitive substring match across shipment id, name, and — when
 *  line detail is available — any line's seller SKU or product name. `lines`
 *  is undefined until that shipment's detail has been fetched (list endpoint
 *  is summary-only); a shipment with unfetched lines still matches on its
 *  own fields, it just can't be found by line content yet. */
export function matchesShipmentSearch(
  shipment: Pick<FbaShipmentSummary, 'shipmentId' | 'name'>,
  lines: ShipmentSearchLine[] | undefined,
  query: string
): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  if (shipment.shipmentId.toLowerCase().includes(trimmed)) return true;
  if (shipment.name?.toLowerCase().includes(trimmed)) return true;
  return (lines ?? []).some(
    (l) =>
      l.sellerSku.toLowerCase().includes(trimmed) ||
      (l.productName?.toLowerCase().includes(trimmed) ?? false)
  );
}

export function searchFbaShipments<
  T extends Pick<FbaShipmentSummary, 'id' | 'shipmentId' | 'name'>,
>(shipments: T[], linesById: Map<string, ShipmentSearchLine[]>, query: string): T[] {
  return shipments.filter((s) => matchesShipmentSearch(s, linesById.get(s.id), query));
}

/**
 * Fetches line detail for the given shipment ids in parallel, ONLY when
 * `enabled` (callers gate this on a non-empty search query so a normal page
 * load doesn't fire N detail requests). Shares the `['fba-shipment', id]`
 * cache key with useFbaShipment, so an already-expanded shipment's lines are
 * reused rather than re-fetched.
 */
export function useShipmentSearchLines(
  shipmentIds: string[],
  enabled: boolean
): Map<string, ShipmentSearchLine[]> {
  const results = useQueries({
    queries: shipmentIds.map((id) => ({
      queryKey: ['fba-shipment', id] as const,
      queryFn: () => apiFetch(`/api/shipments/${id}`, { responseSchema: FbaShipmentDetail }),
      enabled,
      staleTime: 5 * 60 * 1000,
    })),
  });
  return useMemo(() => {
    const map = new Map<string, ShipmentSearchLine[]>();
    shipmentIds.forEach((id, i) => {
      const data = results[i]?.data;
      if (data) {
        map.set(
          id,
          data.lines.map((l) => ({ sellerSku: l.sellerSku, productName: l.productName }))
        );
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentIds, results]);
}

export type ShipmentSortKey = 'shippedAt' | 'status' | 'shipmentId' | 'units';
export type ShipmentSortDirection = 'asc' | 'desc';

const SHIPMENT_STATUS_ORDER: Record<FbaShipmentStatus, number> = {
  preparing: 0,
  shipped: 1,
  receiving: 2,
  closed: 3,
};

type ShipmentSortable = Pick<
  FbaShipmentSummary,
  'shippedAt' | 'status' | 'shipmentId' | 'totalQty'
>;

/** `shippedAt` is nullable (not yet handed to the carrier) — treated as
 *  earliest (epoch 0) so unshipped shipments sort predictably instead of
 *  throwing on Date.parse(null). */
export function compareShipments(
  a: ShipmentSortable,
  b: ShipmentSortable,
  key: ShipmentSortKey,
  direction: ShipmentSortDirection
): number {
  let cmp: number;
  switch (key) {
    case 'shippedAt':
      cmp =
        (a.shippedAt ? Date.parse(a.shippedAt) : 0) - (b.shippedAt ? Date.parse(b.shippedAt) : 0);
      break;
    case 'status':
      cmp = SHIPMENT_STATUS_ORDER[a.status] - SHIPMENT_STATUS_ORDER[b.status];
      break;
    case 'shipmentId':
      cmp = a.shipmentId.localeCompare(b.shipmentId);
      break;
    case 'units':
      cmp = a.totalQty - b.totalQty;
      break;
  }
  return direction === 'asc' ? cmp : -cmp;
}

export function sortFbaShipments<T extends ShipmentSortable>(
  shipments: T[],
  key: ShipmentSortKey,
  direction: ShipmentSortDirection
): T[] {
  return [...shipments].sort((a, b) => compareShipments(a, b, key, direction));
}

// ─────────────────────────────────────────────────────────
// ShipmentsPage filter pills (Faraz, 2026-08-25: "what if I want to see
// only open or pending or any shipment with issues"). Mirrors
// order-planning.ts's Reorder-table filter-pill convention (FilterKey type +
// DEFAULT + PILLS array + matches.../filter.../...Counts trio). Composition
// order in the page is search → filter → sort, matching PlanningPage's
// ReorderSection pipeline.
// ─────────────────────────────────────────────────────────

export type ShipmentFilterKey = 'all' | 'needs_attention' | 'open' | 'closed';

export const DEFAULT_SHIPMENT_FILTER: ShipmentFilterKey = 'all';

export const SHIPMENT_FILTER_PILLS: { key: ShipmentFilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'needs_attention', label: 'Needs attention' },
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
];

type ShipmentFilterable = Pick<
  FbaShipmentSummary,
  'status' | 'createdAt' | 'totalQty' | 'totalReceived' | 'anyUnreceived' | 'unallocatedUnits'
>;

/** "needs_attention" reuses the exact same predicates as the row's
 *  attention badges (ShipmentAttentionBadges in ShipmentsPage.tsx) — the
 *  pill can never disagree with what's actually badged on a row. */
export function matchesShipmentFilter(
  s: ShipmentFilterable,
  filter: ShipmentFilterKey,
  now: Date = new Date()
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'open':
      return s.status !== 'closed';
    case 'closed':
      return s.status === 'closed';
    case 'needs_attention':
      return (
        shortageBadgeLabel(s) != null ||
        unallocatedBadgeLabel(s.unallocatedUnits) != null ||
        isAgingShipment(s.status, s.createdAt, now)
      );
  }
}

export function filterFbaShipments<T extends ShipmentFilterable>(
  shipments: T[],
  filter: ShipmentFilterKey,
  now: Date = new Date()
): T[] {
  return shipments.filter((s) => matchesShipmentFilter(s, filter, now));
}

/** Pure and total: every pill key present even for an empty list (all
 *  zero), so callers can render "Label · 0" without a fallback branch. */
export function shipmentFilterCounts(
  shipments: ShipmentFilterable[],
  now: Date = new Date()
): Record<ShipmentFilterKey, number> {
  const counts: Record<ShipmentFilterKey, number> = {
    all: shipments.length,
    needs_attention: 0,
    open: 0,
    closed: 0,
  };
  for (const s of shipments) {
    if (s.status === 'closed') counts.closed++;
    else counts.open++;
    if (matchesShipmentFilter(s, 'needs_attention', now)) counts.needs_attention++;
  }
  return counts;
}

/** One-click import: pull recent Seller Central shipments into Sourcito. */
export interface ImportResult {
  imported: number;
  skipped: number;
  labelsFetched: number;
  unallocatedUnits: number;
}

export function useImportShipments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<ImportResult>('/api/shipments/import', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fba-shipments'] });
    },
  });
}
